let cloneDeep = require('lodash.clonedeep')
let okeResponse = require('okeoke.standardresponse')

const DiscountType = {
  vip: 1,
  coupons: 2,
  local: 3
}

export default function discountsProcessor(order, offlineEnabled, clientSocket, catalog) {
  function autoApplyDiscounts(orderStart = false) {
    let discounts = []
    if(orderStart) {
      discounts = Object.values(catalog?.discounts || {})
    } else {
      let orderDiscounts = getDiscounts()
      for (let disc of orderDiscounts) {
        let discount = cloneDeep(catalog.discounts[disc.id])
        discounts.push(discount)
      }
    }

    for (let discount of discounts) {
      if (discount?.valid && discount.autoApply) {
        setLocalDiscount(discount)
      }
    }
  }

  function unsetLocalDiscounts() {
    let discounts = getDiscounts()
    for (let discount of discounts) {
      if (discount.type === DiscountType.local) {
        unsetLocalDiscount(discount.id)
      }
    }
  }

  function setLocalDiscountItem(discountID, orderItemUUID, count) {
    let discount = getDiscount(DiscountType.local, discountID)
    let orderItem = order.assembledOrderItems[orderItemUUID]
    if (orderItem == null) return okeResponse(false, 404, 'Order item not found.', null)
    if (orderItem.payment?.amount > 0) return okeResponse(false, 401, 'Item already paid.', null)
    let items = {
      [orderItemUUID]: {
        count: (count == null || count > orderItem.count) ? orderItem.count : count
      }
    }
    if (discount != null) {
      if (items[orderItemUUID].count > 0) {
        let newDiscount = cloneDeep(discount)
        newDiscount = { ...newDiscount, items: { ...newDiscount.items, ...items } }
        return setLocalDiscount(newDiscount)
      } else {
        let newDiscount = cloneDeep(discount)
        delete newDiscount.items[orderItemUUID]
        return setLocalDiscount(newDiscount)
      }
    } else {
      if (items[orderItemUUID].count > 0) {
        return setLocalDiscount({ id: discountID, type: DiscountType.local, items: { ...items } })
      } else return okeResponse(false, 404, 'Nothing to unset.', null)
    }
  }

  function unsetLocalDiscount(discountID) {
    let discount = getDiscount(DiscountType.local, discountID)
    if (discount != null && discount.items != null) {
      let items = {}
      let amount = 0
      for (let orderItem of Object.values(order.assembledOrderItems)) {
        if (orderItem.payment?.amount > 0) {
          let itemExistingDiscount = checkItemDiscount(orderItem.orderItemUUID)
          if (itemExistingDiscount?.id === discount.id) {
            items[orderItem.orderItemUUID] = {
              ...itemExistingDiscount
            }
            amount += items[orderItem.orderItemUUID].count * (items[orderItem.orderItemUUID].unitDiscount + items[orderItem.orderItemUUID].packageDiscount)
          }
        }
      }
      let discountRes = {
        type: DiscountType.local,
        id: discountID,
        items: Object.keys(items || {}).length > 0 ? items : null,
        amount: amount
      }
      return setDiscount(discountRes)
    }
    return okeResponse(false, 404, 'Nothing to unset.', null)
  }

  function checkItemDiscount(orderItemUUID) {
    for (let discount of order.order.discounts) {
      if (discount.type === DiscountType.local) {
        if (discount?.items?.[orderItemUUID]?.count > 0) return { type: discount.type, id: discount.id, ...discount?.items?.[orderItemUUID] }
      }
    }
    return null
  }

  function updateItemDiscount(orderItemUUID) {
    let existingDiscount = checkItemDiscount(orderItemUUID)
    if (existingDiscount != null) {
      let orderItem = order.items.assembleItem(null, orderItemUUID)
      if (existingDiscount?.count !== orderItem?.count || existingDiscount?.unitPrice !== orderItem?.totalPriceUnit) {
        setLocalDiscountItem(existingDiscount.id, orderItemUUID, orderItem.count)
      }
    }
  }

  function setLocalDiscount(discount, user, skipValid = false) { // User for role permission later...
    let assembled = catalog.getDiscount(discount.id)
    if (!skipValid && !assembled.valid) return okeResponse(false, 401, 'Invalid discount.', null)
    let discountItems = {}
    let amount = 0
    if (discount?.items != null) {
      for (let orderItem of Object.values(order.assembledOrderItems)) {
        if (orderItem.payment?.amount > 0) {
          let itemExistingDiscount = checkItemDiscount(orderItem.orderItemUUID)
          if (itemExistingDiscount?.id === discount.id) {
            if (discount.items[orderItem.orderItemUUID] == null) {
              discount.items[orderItem.orderItemUUID] = {
                count: orderItem.count
              }
            }
          } else {
            if (discount.items[orderItem.orderItemUUID]) {
              delete discount.items[orderItem.orderItemUUID]
            }
          }
        }
      }
      for (let [uid, item] of Object.entries(discount?.items)) {
        let orderItem = order.assembledOrderItems[uid]
        let itemExistingDiscount = checkItemDiscount(uid)
        let okToSet = (itemExistingDiscount == null || itemExistingDiscount.id === discount.id)
        if (okToSet && orderItem?.count > 0) {
          if (assembled?.excludeSizes?.[orderItem.selectedSizeUUID] == null && (assembled?.includeSizes == null || assembled?.includeSizes?.[orderItem.selectedSizeUUID] != null)) {
            let unitPrice = (assembled?.excludeModifiers ? orderItem.price : orderItem.sumPriceUnit)
            let unitDiscount = Math.round(unitPrice * (assembled.discountPercent / 100))
            let packageDiscount = (assembled?.includePackaging ? Math.round((orderItem.packagePrice || 0) * (assembled.discountPercent / 100)) : 0)
            discountItems[uid] = {
              unitPrice: orderItem.totalPriceUnit,
              unitDiscount,
              packageDiscount,
              count: (item?.count == null || item.count > orderItem.count) ? orderItem.count : item.count
            }
            amount += discountItems[uid].count * (unitDiscount + packageDiscount)
          }
        }
      }
    } else { // Any item that is defined for discount
      for (let orderItem of Object.values(order.assembledOrderItems)) {
        let itemExistingDiscount = checkItemDiscount(orderItem.orderItemUUID)
        let okToSet = false
        if (orderItem.payment?.amount > 0) {
          okToSet = (itemExistingDiscount.id === discount.id)
        } else {
          okToSet = (itemExistingDiscount == null || itemExistingDiscount.id === discount.id)
        }
        if (okToSet && orderItem.count > 0) {
          let uid = orderItem.orderItemUUID
          if (assembled?.excludeSizes?.[orderItem.selectedSizeUUID] == null && (assembled?.includeSizes == null || assembled?.includeSizes?.[orderItem.selectedSizeUUID] != null)) {
            let unitPrice = (assembled?.excludeModifiers ? orderItem.price : orderItem.sumPriceUnit)
            let unitDiscount = Math.round(unitPrice * (assembled.discountPercent / 100))
            let packageDiscount = (assembled?.includePackaging ? Math.round((orderItem.packagePrice || 0) * (assembled.discountPercent / 100)) : 0)
            discountItems[uid] = {
              unitPrice: orderItem.totalPriceUnit,
              unitDiscount,
              packageDiscount,
              count: orderItem.count
            }
            amount += discountItems[uid].count * (unitDiscount + packageDiscount)
          }
        }
      }
    }
    let discountRes = {
      type: DiscountType.local,
      id: discount.id,
      name: assembled.name,
      items: discountItems,
      amount: amount
    }
    return setDiscount(discountRes)
  }

  function setDiscount(data, orderObj = null, localUpdate = true) {
    let discount = getDiscount(data.type, data.id, orderObj)
    if (discount != null && (JSON.stringify(data) === JSON.stringify(discount))) {
      return okeResponse(false, 401, 'Nothing to set', null)
    }
    let { type, id } = data
    if (localUpdate) {
      order.addUpdate({ discounts: { [type + '-' + id]: data } })
    } else {
      let discount = getDiscount(type, id, orderObj)
      if (discount != null) {
        if (JSON.stringify(data) !== JSON.stringify(discount)) {
          discount.items = data.items
          discount.name = data.name
          discount.amount = data.amount
        }
      } else {
        orderObj ? orderObj.discounts.push(data) : order.order.discounts.push(data)
      }
    }
    return okeResponse(true, 200, 'OK', null)
  }

  function getDiscount(type, id, orderObj = null) {
    if (orderObj) {
      for (let discount of orderObj.discounts) {
        if (discount != null) {
          let match = (discount.type === type && discount.id === id)
          if (match) return discount; // Found
        }
      }
    } else {
      for (let discount of order.order.discounts) {
        if (discount != null) {
          let match = (discount.type === type && discount.id === id)
          if (match) return discount; // Found
        }
      }
    }
    return null; // Not found
  }

  function getDiscounts() {
    let discounts = []
    for (let discount of order.order.discounts) {
      if (discount != null) discounts.push(discount)
    }
    return discounts // Not found
  }

  function calculateDiscounts(res) {
    for (let discount of order.order.discounts) {
      if (discount != null) {
        calculateLocalDiscount(res, discount)
      }
    }

    return res
  }

  async function calculateLocalDiscount(res, discount) {
    let total = 0
    for (let [uid, item] of Object.entries(discount.items || {})) {
      let itemDiscount = {
        name: discount.name,
        type: discount.type,
        id: discount.id,
        amount: item.unitDiscount * item.count,
        packageAmount: item.packageDiscount * item.count
      }
      if (res.items[uid] != null) {
        if (res.items[uid].discounts != null) {
          res.items[uid].discounts.push(itemDiscount)
        } else {
          res.items[uid].discounts = [itemDiscount]
        }
      } else {
        res.items[uid] = {
          discounts: [itemDiscount]
        }
      }
      total += (item.unitDiscount * item.count) + (item.packageDiscount * item.count)
    }
    res.local = {
      total: (res.local?.total || 0) + total
    }
  }

  return {
    setDiscount,
    getDiscount,
    getDiscounts,
    calculateDiscounts,
    setLocalDiscount,
    unsetLocalDiscount,
    unsetLocalDiscounts,
    setLocalDiscountItem,
    updateItemDiscount,
    autoApplyDiscounts
  }
}
